/**
 * Shared site-state derivation for SiteCard, TopBar's site switcher,
 * and the onboarding shell's resume-step picker (ADR-0025 §5).
 *
 * Observable states drive everything UI-facing:
 *   - 'pending_verification' — CNAME not done yet.
 *   - 'awaiting_install'     — verified but `last_seen_at IS NULL` (the
 *                              embed has never pinged).
 *   - 'awaiting_first_ping_shopify_autoinstall' — ADR-0053 §3.2. The
 *                              site is Shopify-OAuth-verified AND the
 *                              ScriptTag auto-install succeeded; the
 *                              merchant has NOT pasted (and need not
 *                              paste) any snippet. The card surfaces
 *                              "recording on first agent visit", not
 *                              "paste the embed snippet."
 *   - 'live'                 — verified + at least one ping has arrived.
 *
 * Anything that reads `Site.status` alone misses the middle states and
 * paints "verified" as "live" — the bug `awaiting_install` was added
 * to surface; the auto-install sub-state was added in ADR-0053 to
 * remove the false "paste the snippet" CTA for Shopify merchants whose
 * embed Sill installed for them via the Admin ScriptTag API.
 */

import type { Site } from '@sill/types';

import { formatRelativeTime } from './format.js';

/**
 * ADR-0053 §3.1 — bounded `embed_install_status` shape exposed by the
 * per-site connector status endpoint for Shopify sites. Optional ctx
 * arg on {@link siteState} so non-shopify callers (and callers without
 * the connector status fetched yet) see the existing two-state
 * behavior. The `?` makes the helper non-breaking.
 */
export type ShopifyEmbedInstallStatus =
  | 'installed'
  | 'scope_denied'
  | 'pending_retry'
  | 'pending'
  | 'not_applicable';

/**
 * Observable UI states. The first value retains the prior name
 * `pending_verification` for back-compat with consumers (TopBar,
 * MobileDrawer, SitesList) — the underlying DB-side enum changed
 * to `pending_proof` (ADR-0043 §4.1) but the dashboard's surface
 * intent is unchanged: "ownership not yet proven."
 */
export type SiteState =
  | 'pending_verification'
  | 'awaiting_install'
  | 'awaiting_first_ping_shopify_autoinstall'
  | 'live';

export interface SiteStateCtx {
  /** ADR-0053 §3.1 — Shopify-only post-OAuth ScriptTag-install outcome.
   *  When `'installed'` the card renders the auto-install copy
   *  (recording on first agent visit); otherwise the existing copy. */
  readonly shopify_embed_install_status?: ShopifyEmbedInstallStatus;
}

export function siteState(site: Site, ctx?: SiteStateCtx): SiteState {
  // ADR-0043 §4.1 — `discovery_active` and `transactional_active` are
  // the two proven-and-live states. Anything else (most notably
  // `pending_proof`) maps to the UI's `pending_verification` bucket.
  if (site.status !== 'discovery_active'
      && site.status !== 'transactional_active') {
    return 'pending_verification';
  }
  if (!site.last_seen_at) {
    // ADR-0053 §3.2 — branch on the Shopify auto-install signal. Only
    // `'installed'` flips the sub-state; `'scope_denied'` and
    // `'pending_retry'` stay in `awaiting_install` so the card surfaces
    // a reconnect / retry affordance (rendered by SiteCard's branching
    // on the same ctx value). Non-shopify sites — and shopify sites
    // whose status the dashboard hasn't fetched yet — fall through to
    // the existing manual-paste copy.
    if (ctx?.shopify_embed_install_status === 'installed') {
      return 'awaiting_first_ping_shopify_autoinstall';
    }
    return 'awaiting_install';
  }
  return 'live';
}

export function isLiveSite(site: Site): boolean {
  return siteState(site) === 'live';
}

/**
 * Mode label for the Sites list + site switcher dropdown (founder
 * 2026-10-20): replaces the generic "DISCOVERY · LIVE" that every site
 * displayed with an honest, derived label.
 *
 * The server's `transactional_active` status is the single source of
 * truth — it is set only when the site has reached capability tier T2
 * (ADR-0043 §3.1), which requires:
 *   - a verified HTTP-challenge proof (payment-engine audit floor), AND
 *   - a verified OAuth domain-match on a connected rail (Stripe or
 *     Shopify), which is what makes "a payment rail connected" true.
 *
 * The baseline guardrail policy is also seeded automatically on the
 * same transition (services/api/src/sites/default-policy.ts), so
 * `transactional_active` always implies both halves of the rule the
 * founder specified ("rail connected AND guardrails active"). A site
 * that is only `discovery_active` has neither, by construction.
 *
 *   - 'transactions_ready' → "Transactions ready" (rail connected +
 *                             baseline policy seeded; mandate engine
 *                             enabled).
 *   - 'discovery'          → "Discovery" (identification + logging
 *                             only; the Phase-1 default).
 */
export type SiteMode = 'discovery' | 'transactions_ready';

export function siteMode(site: Site): SiteMode {
  return site.status === 'transactional_active'
    ? 'transactions_ready'
    : 'discovery';
}

export function siteModeLabel(site: Site): string {
  return siteMode(site) === 'transactions_ready'
    ? 'Transactions ready'
    : 'Discovery';
}

/**
 * Honest, longer-form label for the Sites list card and the site
 * switcher dropdown (founder 2026-10-21). The sill.so case: a site
 * has an active Stripe connector_credential row but is still
 * `discovery_active` because the ADR-0043 §2.6 domain-match evaluated
 * `false` on connect (the Stripe account's `business_profile.url`
 * doesn't match the registered domain and no matching entry sits in
 * `allowed_subdomains`). Rendering a bare "DISCOVERY" there reads to
 * the merchant as "nothing is connected" and is dishonest — the
 * failure mode is "rail connected but the domain-match gate didn't
 * open", which has a specific fix (Stripe business URL or
 * allowed_subdomains).
 *
 * Shape:
 *   - transactional_active                     → "Transactions ready"
 *   - discovery + any `connected_rails` entry  → "Stripe connected · not taking orders yet"
 *                                                (or the first connected rail's capitalised
 *                                                name — a merchant with Shopify+Stripe sees
 *                                                "Shopify connected · ...").
 *   - discovery + no connected rail            → "Discovery"
 *
 * `connected_rails` is `undefined` on older API responses — treated
 * identically to "none connected" so the label never falsely implies
 * a rail is connected.
 */
export function siteModeDetailLabel(site: Site): string {
  if (siteMode(site) === 'transactions_ready') return 'Transactions ready';
  const first = site.connected_rails?.[0];
  if (first === undefined) return 'Discovery';
  const name =
    first === 'stripe' ? 'Stripe'
      : first === 'shopify' ? 'Shopify'
        : 'WooCommerce';
  return `${name} connected · not taking orders yet`;
}

/** A previously-live site is considered "stale" once it has gone this
 *  long with no ingest beacon — the threshold the SiteCard freshness
 *  strip and the Mandates empty-state both key off. The single most
 *  common cause is a key-rotation drift between the deployed embed
 *  snippet and `site.site_key_hash`. */
export const SITE_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * Returns a stale-signal payload ONLY when `last_activity_at` is older
 * than {@link SITE_STALE_AFTER_MS}; `null` for fresh, never-seen, or
 * unparseable timestamps. Single-sourced here so the Sites view and the
 * Mandates view agree on the threshold and the relative label.
 *
 * Callers gate on liveness themselves (`isLiveSite`) — a site that has
 * never pinged has no "went quiet" story to tell, only an
 * awaiting-install one.
 */
export function siteStaleSignal(
  last_activity_at: string | null | undefined,
): { age_label: string } | null {
  if (last_activity_at === undefined || last_activity_at === null) return null;
  const last_ms = new Date(last_activity_at).getTime();
  if (!Number.isFinite(last_ms)) return null;
  const age_ms = Math.max(0, Date.now() - last_ms);
  if (age_ms < SITE_STALE_AFTER_MS) return null;
  return { age_label: formatRelativeTime(last_activity_at) };
}
